import { Box, Button, Flex } from "@chakra-ui/react";
import { ProtectedImage } from "../ProtectedImage";
import { Bundle, LayoutStyle, DisplayFlags, Price, Section } from "../../types";
import { SharpCornerBox } from "./components/SharpCornerBox";
import { TimerBadge } from "./components/TimerBadge";
import { BundleImageStack } from "./components/BundleImageStack";
import { BundleCardInformationSection } from "./components/BundleCardInformationSection";
import { AllReleasesOwnedBadge } from "./components/AllReleasesOwnedBadge";
import { useBundlePricing } from "../../hooks/useBundlePricing";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { t } from "i18next";
import { useGridCardDimensions } from "./hooks/useGridCardDimensions";
import { trackRecsysCardItemClick } from "../../analytics/recsys/tracking";

export type BundleContentCardProps = {
  content: Bundle;
  layout: {
    style: LayoutStyle;
    display: DisplayFlags;
  };
  fullWidth?: boolean;
  section?: Section;
  index?: number;
  sectionIndex?: number;
};

export type BundleCardProps = {
  content: Bundle;
  layout: {
    style: LayoutStyle;
    display: DisplayFlags;
  };
  priceToUse: Price;
  discountPercentage: number;
  hasDiscount: boolean;
  shouldShowTimer: boolean;
  artistImageUrl?: string;
  artistName?: string;
  handleBundleClick: (event: React.MouseEvent) => void;
  handleArtistPictureClick?: (event: React.MouseEvent) => void;
  width?: number;
  height?: number;
  expanded?: boolean;
};

export function BundleCard({
  content,
  layout: { display, style },
  section,
  index,
  sectionIndex,
  expanded,
  ...props
}: BundleContentCardProps & { expanded?: boolean }) {
  const layout_size = style.size;
  const { openBundleDetails, openArtistProfile } = useSlideInMenu();
  const { priceToUse, discountPercentage, hasDiscount } =
    useBundlePricing(content);

  const { height, width } = useGridCardDimensions(
    "bundle",
    style.mode,
    layout_size,
  );

  const { avatar_url: artistImageUrl, name: artistName } = content.owner || {};

  const shouldShowTimer = () => {
    if (!content.expires_on) return false;
    const date = new Date(content.expires_on);
    const now = new Date();
    const daysRemaining = Math.ceil(
      (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );
    return daysRemaining > 0;
  };

  const handleArtistPictureClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (content.owner_id) {
      openArtistProfile(content.owner_id);
    }
  };

  const handleBundleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (section && sectionIndex !== undefined) {
      trackRecsysCardItemClick({
        section,
        sectionIndex,
        item: content,
        itemIndex: index ?? 0,
      });
    }
    openBundleDetails(content.uuid);
  };

  const sharedProps: BundleCardProps = {
    content,
    layout: { display, style },
    priceToUse,
    discountPercentage,
    hasDiscount,
    shouldShowTimer: shouldShowTimer(),
    artistImageUrl,
    artistName,
    handleBundleClick,
    handleArtistPictureClick,
    width,
    height,
    expanded,
  };

  switch (layout_size) {
    case "s":
      return <ListBundleCard {...sharedProps} expanded={expanded} {...props} />;
    case "m":
    case "l":
    case "xl":
      return <GridBundleCard {...sharedProps} {...props} />;
    default:
      console.warn("Unsupported bundle card size", layout_size);
      return null;
  }
}

function GridBundleCard({
  content,
  priceToUse,
  discountPercentage,
  hasDiscount,
  shouldShowTimer,
  artistImageUrl,
  artistName,
  handleBundleClick,
  handleArtistPictureClick,
}: BundleCardProps) {
  const creatorImageSize = shouldShowTimer ? "35px" : "66px";
  const userOwnsAllItems = content.user_owns_all_items;

  return (
    <Flex
      direction="column"
      position="relative"
      width="100%"
      height="auto"
      bg="charcoal.900"
      p="16px"
      borderRadius="16px"
      cursor="pointer"
      onClick={handleBundleClick}
    >
      {hasDiscount && !userOwnsAllItems && (
        <SharpCornerBox
          position="absolute"
          top={0}
          corners={["topLeft", "bottomRight"]}
          fontSize="14px"
          fontWeight="bold"
          background="success.700"
          color="neutral.white"
          right={0}
          px={2}
          py={1}
          zIndex={50}
        >
          {discountPercentage}% OFF
        </SharpCornerBox>
      )}
      {/* Cover image */}
      <Box position="relative">
        <BundleImageStack
          items={content.items}
          totalBundleItems={content.total_items}
        />
      </Box>

      <Flex direction="row" position="relative" width="100%" mt={2} gap={3}>
        {/* Left Column */}
        <Flex direction="column" flex={1} minW={0} gap={2}>
          <BundleCardInformationSection
            content={content}
            priceToUse={priceToUse}
            discountPercentage={discountPercentage}
            showName
            showReleasesPages
            showDescription={false}
            nameNoOfLines={1}
            showPrice={!userOwnsAllItems}
          />
          {userOwnsAllItems && <AllReleasesOwnedBadge size="sm" thickIcon />}
        </Flex>

        {/* Right Column */}
        <Flex direction="column" align="flex-end" gap={2} flexShrink={0}>
          {content.owner && artistImageUrl && (
            <Box
              boxSize={creatorImageSize}
              borderRadius="8px"
              overflow="hidden"
              border="1px solid"
              borderColor="transparent.white.10"
            >
              <ProtectedImage
                src={artistImageUrl}
                w="100%"
                h="100%"
                objectFit="cover"
                alt={`${artistName} avatar`}
                onClick={handleArtistPictureClick}
              />
            </Box>
          )}
          {content.expires_on && (
            <TimerBadge availableUntil={content.expires_on} />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}

function DiscountBadge({
  discountPercentage,
  fontSize,
  mt,
  flexShrink,
}: {
  discountPercentage: number;
  fontSize: string;
  mt?: number;
  flexShrink?: number;
}) {
  return (
    <SharpCornerBox
      corners={[]}
      fontSize={fontSize}
      borderRadius="4px"
      fontWeight="bold"
      background="success.700"
      color="neutral.white"
      px={2}
      py={1}
      mt={mt}
      flexShrink={flexShrink}
    >
      {t("components.slideInMenu.bundleDetails.discountOff", {
        discountPercentage,
      })}
    </SharpCornerBox>
  );
}

function ListBundleCardContent({
  content,
  priceToUse,
  discountPercentage,
  hasDiscount,
  expanded,
  handleButtonClick,
}: {
  content: Bundle;
  priceToUse: Price;
  discountPercentage: number;
  hasDiscount: boolean;
  expanded: boolean;
  handleButtonClick: (event: React.MouseEvent) => void;
}) {
  const userOwnsAllItems = content.user_owns_all_items;

  if (expanded) {
    return (
      <>
        <Flex
          direction="row"
          align="flex-start"
          justify="space-between"
          gap={3}
        >
          <Box
            flex={1}
            pb={userOwnsAllItems ? "4px" : "16px"}
            mt={userOwnsAllItems ? undefined : "24px"}
          >
            <BundleCardInformationSection
              content={content}
              priceToUse={priceToUse}
              discountPercentage={discountPercentage}
              showReleasesPages={false}
              showDescription={true}
              nameNoOfLines={2}
              showPrice={!userOwnsAllItems}
            />
          </Box>
          {hasDiscount && !userOwnsAllItems && (
            <DiscountBadge
              discountPercentage={discountPercentage}
              fontSize="12px"
              flexShrink={0}
            />
          )}
        </Flex>

        <Flex
          direction="row"
          align="center"
          justify="flex-start"
          gap={3}
          mt={0}
        >
          {userOwnsAllItems ? (
            <AllReleasesOwnedBadge size="sm" thickIcon />
          ) : (
            <>
              <Button
                variant="primary"
                size="sm"
                maxWidth="120px"
                minWidth="80px"
                flex="1 1 auto"
                onClick={handleButtonClick}
                isDisabled={false}
              >
                {t("components.slideInMenu.bundleDetails.buyBundleNow")}
              </Button>
              <TimerBadge availableUntil={content.expires_on} />
            </>
          )}
        </Flex>
      </>
    );
  }

  return (
    <>
      <Flex align="flex-start" justify="flex-start">
        {hasDiscount && !userOwnsAllItems && (
          <DiscountBadge
            discountPercentage={discountPercentage}
            fontSize="10px"
            mt={1}
          />
        )}
      </Flex>

      <Box>
        <BundleCardInformationSection
          content={content}
          priceToUse={priceToUse}
          discountPercentage={discountPercentage}
          showReleasesPages={false}
          showDescription={false}
          nameNoOfLines={1}
          namePaddingTop="12px"
          showPrice={!userOwnsAllItems}
        />
      </Box>

      <Flex
        direction="column"
        align="flex-start"
        justify={!content.expires_on ? "flex-end" : "flex-start"}
        gap={2}
        mt={userOwnsAllItems ? undefined : "auto"}
      >
        {userOwnsAllItems ? (
          <AllReleasesOwnedBadge size="sm" mt="6px" thickIcon />
        ) : (
          <>
            <TimerBadge availableUntil={content.expires_on} />
            <Button
              variant="primary"
              size="sm"
              height="26px"
              maxWidth="120px"
              minWidth="80px"
              flex="1 1 auto"
              mb={!content.expires_on ? "12px" : undefined}
              onClick={handleButtonClick}
              isDisabled={false}
            >
              {t("components.slideInMenu.bundleDetails.buyBundleNow")}
            </Button>
          </>
        )}
      </Flex>
    </>
  );
}

function ListBundleCard({
  content,
  layout: { display },
  priceToUse,
  discountPercentage,
  hasDiscount,
  handleBundleClick,
  width,
  height,
  expanded = false,
}: BundleCardProps & { expanded?: boolean }) {
  const { include_cover_image } = display;

  return (
    <Flex
      direction="row"
      position="relative"
      alignItems="flex-start"
      justifyContent="start"
      p={2}
      pr={expanded ? 2 : 4}
      bg="charcoal.900"
      borderRadius="16px"
      width={expanded ? "100%" : width ? `${width}px` : "auto"}
      height={height ? `${height}px` : "181px"}
      gap="24px"
      cursor="pointer"
      onClick={handleBundleClick}
    >
      {include_cover_image && (
        <Box position="relative" flexShrink={0} width="fit-content">
          <BundleImageStack
            items={content.items}
            layout="list"
            totalPages={content.items.reduce(
              (sum, item) => sum + item.release_page_count,
              0,
            )}
            totalReleases={content.items.length}
          />
        </Box>
      )}

      <Flex
        direction="column"
        flex={1}
        minW={0}
        height={!expanded ? "100%" : undefined}
        justifyContent="center"
      >
        <ListBundleCardContent
          content={content}
          priceToUse={priceToUse}
          discountPercentage={discountPercentage}
          hasDiscount={hasDiscount}
          expanded={expanded}
          handleButtonClick={handleBundleClick}
        />
      </Flex>
    </Flex>
  );
}
